import React, {useEffect, useCallback, useMemo, useRef, useState, memo} from 'react';
import {useSelector} from 'react-redux';
import styled from 'styled-components';
import {IconoirEnlarge, IconoirReduce} from '@ey-xd/motif-icon';
import env from '../../../../util/env';
import {financialStatementType} from '../../../../util/uiconstants';
import {clientSideEventNames, trialBalanceType} from '../../../../util/uiconstants';
import {labels} from '../../../../util/utils';
import ProgressBar from '../../BaseComponentWrappers/MotifProgressBar';
import TextLink from '../../BaseComponentWrappers/MotifTextLink';
import MotifTooltip from '../../BaseComponentWrappers/MotifTooltip';
import OARNoRowsOverlay from './OARNoRowsOverlay';
import InlineAnnotation from '../../../Common/OAR/InlineAnnotation';
import OARTableFooter from './OARTableFooter';
import Button from '../../../Common/BaseComponentWrappers/MotifButton';
import SwitchableOARTableWrapper from './SwitchableOARTableWrapper';
import IconButton from '../../BaseComponentWrappers/MotifIconButton';
import {isEqual} from 'lodash';
import {useBlackListAnalyzerTypes} from '../../../../util/customHooks';

function OARTableWrapper({
	columnDefs,
	frameworkComponents,
	autoGroupColumnDef,
	getDataRow,
	trialBalanceData,
	eyHelixLink,
	engagementId,
	openModal,
	trialBalance,
	showModal,
	onTypeChange,
	type,
	isDisabled
}) {
	const containerStyle = useMemo(() => ({width: '100%', height: '100%'}), []);
	const [isExpanded, setIsExpanded] = useState(true);
	const hasHelixProjects = useSelector((store) => store.configurations?.features?.hasHelixProjects);
	const gridRef = useRef(null);
	const useBlackListAnalyzerTypesValue = useBlackListAnalyzerTypes();

	const handleOnClickNavigate = useCallback(
		(event) => {
			event.preventDefault();
			window.open(env.getURL('hercules_web') + '/connections?engagementid=' + engagementId);
		},
		[engagementId]
	);

	const gridStyle = useMemo(() => ({height: '100%', width: '100%'}), []);

	const headerComponentParams = useMemo(() => {
		return {
			template: `
					<div class="ag-cell-label-container" role="presentation">
						<span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
						<div ref="eLabel" class="ag-header-cell-label" role="presentation">
							<span ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>
							<span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>
							<span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>
							<span ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
							<span ref="eText" class="ag-header-cell-text" role="columnheader" style="white-space: normal;"></span>
							<span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
						</div>
					</div>`
		};
	}, []);

	useEffect(() => {
		setIsExpanded(true);
	}, [trialBalanceData]);

	const defaultColDef = useMemo(() => {
		return {
			minWidth: 100,
			sortable: false,
			resizable: false,
			suppressHeaderMenuButton: true,
			wrapText: false,
			autoHeight: true,
			suppressMovable: true,
			headerComponentParams: headerComponentParams
		};
	}, [headerComponentParams]);

	const getDataPath = useMemo(() => {
		return (data) => {
			return data?.arc;
		};
	}, []);

	const getHelixLinkTitleTip = useCallback(() => {
		if (eyHelixLink === '') {
			const setupText = hasHelixProjects ? labels.oarSetupText.split('{0}') : labels.helixNotAvailable;
			return (
				<>
					{hasHelixProjects ? (
						<>
							<span className="TextLabel">{setupText[0]}</span>
							<Button
								variant="text-alt"
								className="helixSetURL"
								onClick={handleOnClickNavigate}
								showLabel
								isEllipsed
								buttonLabel={labels.eyHelixProjects}
								noOfLines={1}
								disabled={isDisabled}
							/>
							<span className="TextLabel">{setupText[1]}</span>
						</>
					) : (
						<>
							<span className="TextLabel">{setupText}</span>
						</>
					)}
				</>
			);
		}
	}, [eyHelixLink, handleOnClickNavigate, hasHelixProjects]);

	const getRowClass = useCallback((params) => {
		if (
			params.data &&
			params.data.financialStatementSubTypeId === financialStatementType.AccountClass &&
			params.data.annotations.length > 0
		) {
			return 'row-annotation';
		}

		if (params.node.data.fullWidthRow) {
			return 'customScrollbar';
		}
	}, []);

	const getRowHeight = useCallback((params) => {
		if (params.data && params.data.fullWidthRow) {
			// set here row height needed when annotation is there
			return 160;
		}
	}, []);

	const isFullWidthRow = (params) => {
		return params.rowNode.data.fullWidthRow;
	};

	const noRowsOverlayComponent = useMemo(() => {
		return OARNoRowsOverlay;
	}, []);

	const expandAnnotations = useCallback(() => {
		if (gridRef.current) {
			gridRef.current.forEachNode((node) => {
				if (node.level === 2) {
					node.setExpanded(!isExpanded);
					setIsExpanded(!isExpanded);
				}
			});
		}
	}, [isExpanded]);

	const isModalOpen = useCallback(() => {
		if (gridRef.current) {
			gridRef.current.forEachNode((node) => {
				if (node.level === 2) {
					node.setExpanded(true);
					setIsExpanded(true);
				}
			});
		}
	}, []);

	useEffect(() => {
		window.addEventListener(clientSideEventNames.updateExpandWhenModalIsOpen, isModalOpen);
		return () => {
			window.removeEventListener(clientSideEventNames.updateExpandWhenModalIsOpen, isModalOpen);
		};
	}, [isModalOpen, showModal]);

	const inlineLinks = useCallback(
		(tab) => {
			return (
				<section className={`helixLinkWrapper`}>
					{!useBlackListAnalyzerTypesValue && tab === trialBalanceType.Planning && (
						<section className={`${eyHelixLink !== '' ? 'EnabledHelixLink' : 'DisabledHelixLink'}`}>
							{eyHelixLink === '' ? (
								<MotifTooltip
									className="OARHelixTooltip"
									variant="alt"
									trigger={labels.OARHelixNavigationLink}
									hideCloseButton
								>
									{getHelixLinkTitleTip()}
								</MotifTooltip>
							) : (
								<TextLink
									disabled={eyHelixLink === ''}
									variant="alt"
									href={eyHelixLink}
									label={labels.OARHelixNavigationLink}
									target={'_blank'}
									className="helixSetURLEnabled"
									isEllipsed
								/>
							)}
							{trialBalanceData.length > 0 && <span className="LinkSeparator">|</span>}
						</section>
					)}
					{trialBalanceData.length > 0 && (
						<section
							className={`AnnotationsButton ${
								trialBalanceData.length > 0 ? 'EnabledExpandCollapse' : 'DisabledExpandCollapse'
							}`}
							onClick={expandAnnotations}
						>
							<IconButton
								type="button"
								buttonLabel={!isExpanded ? labels.OARExpand : labels.OARCollapse}
								iconFunction={isExpanded ? IconoirReduce : IconoirEnlarge}
								showLabel
								isEllipsed
								variant="text-alt"
							/>
						</section>
					)}
				</section>
			);
		},
		[eyHelixLink, getHelixLinkTitleTip, trialBalanceData.length, expandAnnotations, isExpanded]
	);

	const _InlineAnnotation = useCallback(
		(agGridParams) => {
			return (
				<InlineAnnotation {...agGridParams} showButtons={type === trialBalanceType.Planning} isDisabled={isDisabled} />
			);
		},
		[type]
	);

	const handleOnGridReady = useCallback((params) => {
		gridRef.current = params.api;
	}, []);

	const fullWidthCellRendererParams = useMemo(() => {
		return {
			openModal: openModal
		};
	}, [openModal]);

	return (
		<StyledOARTableWrapper className="StyledOARTableWrapper">
			<section style={containerStyle} className="AgGridContainerStyles">
				<section style={gridStyle} className="AGGridWrapperSection">
					<SwitchableOARTableWrapper
						ref={gridRef}
						getDataRow={getDataRow}
						onTypeChange={onTypeChange}
						rightControls={inlineLinks}
						getRowHeight={getRowHeight}
						getRowClass={getRowClass}
						onReady={handleOnGridReady}
						rowData={trialBalanceData.length > 0 ? trialBalanceData : []}
						columnDefs={trialBalanceData.length > 0 ? columnDefs : []}
						autoGroupColumnDef={autoGroupColumnDef}
						defaultColDef={defaultColDef}
						animateRows={false}
						debounceVerticalScrollbar
						suppressRowHoverHighlight
						groupDefaultExpanded={3}
						groupDisplayType={'singleColumn'}
						noRowsOverlayComponent={noRowsOverlayComponent}
						noRowsOverlayComponentParams={{engagementId: engagementId, hasHelixProjects: hasHelixProjects}}
						components={frameworkComponents}
						suppressRowClickSelection
						suppressHorizontalScroll={false}
						suppressCellFocus
						wrapHeaderText
						suppressGroupRowsSticky
						autoHeaderHeight
						suppressContextMenu
						loadingOverlayComponent={ProgressBar}
						loadingOverlayComponentParams={{
							isIndeterminate: true,
							circle: true
						}}
						suppressAggFuncInHeader
						domLayout="normal"
						getDataPath={getDataPath}
						treeData
						isFullWidthRow={isFullWidthRow}
						fullWidthCellRenderer={_InlineAnnotation}
						fullWidthCellRendererParams={fullWidthCellRendererParams}
						containerClassName="customScrollbar"
						enableCellTextSelection
						isDisabled={isDisabled}
					/>
				</section>
			</section>
			{trialBalance && <OARTableFooter trialBalanceAttributeJson={trialBalance?.trialBalanceAttributeJson} />}
		</StyledOARTableWrapper>
	);
}

export default memo(OARTableWrapper, isEqual);

const StyledOARTableWrapper = styled.section`
	.AGGridWrapperSection {
		.StyledAGGridEnterprise {
			max-height: 70rem;
			overflow: auto;
		}
		.ag-ltr {
			.ag-row-group-indent-0,
			.ag-row-group-indent-1,
			.ag-row-group-indent-2 {
				align-items: center;
				.ag-group-expanded,
				.ag-group-contracted {
					min-height: var(--px-22);
					height: auto;
					margin-right: var(--px-5);
					.ag-icon {
						&::before {
							fill: var(--neutrals-900);
							color: var(--neutrals-900);
						}
					}
				}
			}
			.ag-row-group-indent-1 {
				padding-left: var(--px-20);
			}
			.ag-row-group-indent-2 {
				align-items: center;
				padding-left: var(--px-45);
			}
			.ag-row-group-leaf-indent {
				margin-left: var(--px-20);
				&.ag-row-group-indent-2 {
					padding-left: var(--px-48);
				}
				&.ag-row-group-indent-3 {
					padding-left: var(--px-85);
					margin-left: var(--px-0);
					.StyledNameColumn {
						font-size: var(--px-12) !important;
					}
				}
			}
		}
		.ag-header {
			height: var(--px-58) !important;
			.ag-header-viewport {
				.ag-header-container {
					.ag-header-row {
						height: var(--px-58) !important;
						.ag-header-group-cell {
							font-size: var(--px-14);
							font-weight: 400;
							padding: 0 0 0 var(--px-10);
							justify-content: flex-end;
							&[col-id='7_0'] {
								justify-content: center;
								.StyledIconColumnHeader {
									justify-content: center;
								}
							}
						}
						.ag-header-cell {
							font-size: var(--px-14);
							font-weight: 400;
							padding: var(--px-5) var(--px-8);
							height: var(--px-58) !important;
							.ag-header-cell-comp-wrapper {
								justify-content: flex-end;
							}
							&[col-id='ag-Grid-AutoColumn'] {
								padding-left: var(--px-24);
								.ag-header-cell-text,
								.ag-header-cell-comp-wrapper {
									font-size: var(--px-14) !important;
									font-weight: 400 !important;
									line-height: var(--px-24) !important;
									justify-content: flex-start !important;
									text-align: left;
									color: var(--neutrals-900);
								}
							}
							&[col-id='analysisPeriodBalance'] {
								.ag-header-cell-label,
								.ag-header-cell-comp-wrapper {
									justify-content: flex-end;
									text-align: right;
									color: var(--neutrals-900);
								}
							}
							&[col-id='analysisComparativeBalanceChange'] {
								.ag-header-cell-label,
								.ag-header-cell-comp-wrapper {
									justify-content: flex-end;
									text-align: right;
									color: var(--neutrals-900);
								}
							}
							&[col-id='comparativePeriodBalance'] {
								.ag-header-cell-label,
								.ag-header-cell-comp-wrapper {
									justify-content: flex-end;
									text-align: right;
									color: var(--neutrals-900);
								}
							}
							&[col-id='secondComparativePeriodBalance'] {
								.ag-header-cell-label,
								.ag-header-cell-comp-wrapper {
									justify-content: flex-end;
									text-align: right;
									color: var(--neutrals-900);
								}
							}
							&[col-id='analysisComparativeBalanceChangePercent'] {
								.ag-header-cell-label,
								.ag-header-cell-comp-wrapper {
									justify-content: flex-end;
									text-align: right;
									color: var(--neutrals-900);
								}
							}
							&[col-id='analysisSecondComparativeBalanceChange'] {
								.ag-header-cell-label .ag-header-cell-comp-wrapper {
									justify-content: flex-end;
									text-align: right;
									color: var(--neutrals-900);
								}
							}
							&[col-id='analysisSecondComparativeBalanceChangePercent'] {
								.ag-header-cell-label,
								.ag-header-cell-comp-wrapper {
									justify-content: flex-end;
									text-align: right;
									color: var(--neutrals-900);
								}
							}
							&[col-id='id'] {
								.ag-header-cell-label,
								.ag-header-cell-comp-wrapper {
									justify-content: center;
									text-align: center;
									color: var(--neutrals-900);
								}
							}
						}
					}
				}
			}
		}
		.ag-body-viewport {
			.ag-full-width-container {
				.ag-row {
					&.ag-row-level-3 {
						padding: 0 var(--px-110) !important;
						overflow-y: auto;
					}
					&.ag-full-width-row {
						overflow: auto;
						max-height: 15rem;
					}
				}
			}
			.ag-row {
				&.ag-row-level-0 {
					.ag-cell {
						&[col-id='ag-Grid-AutoColumn'] {
							.StyledNameColumn {
								font-size: var(--px-14);
								font-weight: 400;
								color: var(--neutrals-900);
							}
						}
						&[col-id='id'] {
							.StyledIconColumn {
								cursor: default;
								svg {
									cursor: default;
								}
							}
						}
					}
				}
				&.ag-row-level-1 {
					.ag-cell {
						&[col-id='ag-Grid-AutoColumn'] {
							.StyledNameColumn {
								font-size: var(--px-14);
								font-weight: 300;
								color: var(--neutrals-900);
							}
						}
						&[col-id='id'] {
							.StyledIconColumn {
								cursor: default;
								svg {
									cursor: default;
								}
							}
						}
					}
				}
				&.ag-row-level-2 {
					.ag-cell {
						&[col-id='ag-Grid-AutoColumn'] {
							.StyledNameColumn {
								font-size: var(--px-12);
								font-weight: 300;
								color: var(--neutrals-900);
							}
						}
					}
				}
				&.ag-row-level-3 {
					--overflow-y: auto !important;
					padding: 0 0 0 var(--px-110) !important;
				}
				.ag-cell {
					&[col-id='ag-Grid-AutoColumn'] {
						padding-left: var(--px-24);
						color: var(--neutrals-900);
					}
					&[col-id='analysisPeriodBalance'] {
						color: var(--neutrals-900);
					}
					&[col-id='analysisComparativeBalanceChange'] {
						color: var(--neutrals-900);
						border-left: var(--px-2) solid var(--neutrals-200);
					}
					&[col-id='comparativePeriodBalance'] {
						color: var(--neutrals-900);
					}
					&[col-id='secondComparativePeriodBalance'] {
						color: var(--neutrals-900);
					}
					&[col-id='analysisComparativeBalanceChangePercent'] {
						color: var(--neutrals-900);
					}
					&[col-id='analysisSecondComparativeBalanceChange'] {
						color: var(--neutrals-900);
						border-left: var(--px-2) solid var(--neutrals-200);
					}
					&[col-id='analysisSecondComparativeBalanceChangePercent'] {
						color: var(--neutrals-900);
					}
					&[col-id='id'] {
						display: flex;
						align-items: center;
						justify-content: center;
						text-align: center;
						color: var(--neutrals-900);
						border-left: var(--px-2) solid var(--neutrals-200);
						svg {
							color: var(--neutrals-900);
						}
					}
				}
			}
		}
	}
	.helixLinkWrapper {
		display: flex;
		margin: 0;
		.DisabledHelixLink {
			color: var(--neutrals-700);
		}
		.LinkSeparator {
			margin: 0 var(--px-10);
			color: var(--blue-600);
			font-weight: bold;
			line-height: var(--px-22);
		}
		.AnnotationsButton {
			cursor: pointer;
			color: var(--blue-600);
			&.DisabledExpandCollapse {
				color: var(--neutrals-700);
				pointer-events: none;
			}
		}
	}
`;
